import React, { useContext, useState } from 'react';
import {
  CircularProgress,
  Container,
  Grid,
  Switch,
  Typography
} from '@mui/material';
import TopologyColumn from './topologyColumn';
import {
  getAmount,
  getHighestConnectionValue,
  JSONRecordData
} from '../Log/graph.util';
import { ProductIdContext } from '../../../contexts/ProductIdContextProvider';

interface TopologyProps {
  values: JSONRecordData;
  connected: boolean;
  loading: boolean;
}

function Topology(props: TopologyProps) {
  if (props.values === null && props.connected == true) {
    return null;
  }
  const highestConnectionValue =
    props.values != null ? getHighestConnectionValue(props.values) : 0;

  const { product, setProduct } = useContext(ProductIdContext);

  const [showValues, setShowValues] = useState(false);

  const handleSwitch = () => () => {
    setShowValues(!showValues);
  };

  const isMobile = window.innerWidth <= 1490;

  const totalPvPower = props.values?.PvOnDc
    ? Object.values(props.values.PvOnDc).reduce(
        (sum, device) => sum + (device?.Dc?.Power || 0),
        0
      )
    : 0;

  return (
    <Container maxWidth="xl" style={{ backgroundColor: 'white' }}>
      <Grid container>
        {!props.connected && !props.loading && (
          <Container
            maxWidth="xl"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '70vh'
            }}
          >
            <CircularProgress size={60} style={{ color: '#ffc04d' }} />
            <Typography
              variant="body2"
              style={{ color: 'black', fontWeight: 'bold' }}
              mt={2}
            >
              Unable to communicate with the installation
            </Typography>
            <Typography variant="body2" style={{ color: 'black' }}>
              Please wait or refresh the page
            </Typography>
          </Container>
        )}

        {props.connected && (
          <>
            <Grid
              item
              xs={12}
              md={12}
              style={{
                marginTop: '10px',
                height: '20px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'right',
                justifyContent: 'right'
              }}
            >
              <div>
                <Typography sx={{ marginTop: '5px', marginRight: '20px' }}>
                  Display Values
                </Typography>
                <Switch
                  edge="start"
                  color="secondary"
                  onChange={handleSwitch()}
                  sx={{
                    '& .MuiSwitch-thumb': {
                      backgroundColor: 'orange'
                    },
                    marginLeft: '20px'
                  }}
                />
              </div>
            </Grid>

            <Grid
              item
              xs={12}
              md={12}
              style={{
                height: isMobile ? '550px' : '600px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <TopologyColumn
                centerBox={{
                  title: 'Grid',
                  data: props.values?.GridMeter
                    ? [
                        {
                          value: props.values.GridMeter.Ac.L1.Power.Active,
                          unit: 'W'
                        },
                        {
                          value: props.values.GridMeter.Ac.L2.Power.Active,
                          unit: 'W'
                        },
                        {
                          value: props.values.GridMeter.Ac.L3.Power.Active,
                          unit: 'W'
                        }
                      ]
                    : undefined,
                  connected:
                    props.values.Config.Devices.GridMeterIp.DeviceState !=
                    'Disabled'
                }}
                centerConnection={{
                  orientation: 'horizontal',
                  data: props.values?.GridMeter
                    ? {
                        value: props.values.GridMeter.Ac.Power.Active,
                        unit: 'W'
                      }
                    : undefined,
                  amount: props.values?.GridMeter
                    ? getAmount(
                        highestConnectionValue,
                        props.values.GridMeter.Ac.Power.Active
                      )
                    : 0,
                  showValues: showValues
                }}
                isLast={false}
                isFirst={true}
              />
              {/*-------------------------------------------------------------------------------------------------------------------------------------------------------------*/}
              <TopologyColumn
                topBox={{
                  title: 'Pv Inverter',
                  data: props.values?.PvOnAcGrid
                    ? [
                        {
                          value: props.values.PvOnAcGrid.Power.Active,
                          unit: 'W'
                        }
                      ]
                    : undefined,
                  connected:
                    props.values.Config.Devices.PvOnAcGrid.DeviceState !==
                    'Disabled'
                }}
                topConnection={{
                  orientation: 'vertical',
                  position: 'top',
                  data: props.values?.PvOnAcGrid
                    ? {
                        value: props.values.PvOnAcGrid.Power.Active,
                        unit: 'W'
                      }
                    : undefined,
                  amount: props.values?.PvOnAcGrid
                    ? getAmount(
                        highestConnectionValue,
                        props.values.PvOnAcGrid.Power.Active
                      )
                    : 0,
                  showValues: showValues
                }}
                centerBox={{
                  title: 'Grid Bus',
                  data: props.values?.GridMeter
                    ? [
                        {
                          value: props.values.GridMeter.Ac.L1.Voltage,
                          unit: 'V'
                        },
                        {
                          value: props.values.GridMeter.Ac.L2.Voltage,
                          unit: 'V'
                        },
                        {
                          value: props.values.GridMeter.Ac.L3.Voltage,
                          unit: 'V'
                        }
                      ]
                    : undefined,
                  connected: true
                }}
                centerConnection={{
                  orientation: 'horizontal',

                  data: props.values?.AcGridToAcIsland
                    ? {
                        value: props.values.AcGridToAcIsland.Power.Active,
                        unit: 'W'
                      }
                    : undefined,
                  amount: props.values?.AcGridToAcIsland
                    ? getAmount(
                        highestConnectionValue,
                        props.values.AcGridToAcIsland.Power.Active
                      )
                    : 0,
                  showValues: showValues
                }}
                bottomBox={{
                  title: 'AC Loads',
                  data: props.values?.LoadOnAcGrid
                    ? [
                        {
                          value: props.values.LoadOnAcGrid.Power.Active,
                          unit: 'W'
                        }
                      ]
                    : undefined,
                  connected:
                    props.values.Config.Devices.LoadOnAcGrid.DeviceState !=
                    'Disabled'
                }}
                bottomConnection={{
                  orientation: 'vertical',
                  position: 'bottom',
                  data: props.values?.LoadOnAcGrid
                    ? {
                        value: props.values.LoadOnAcGrid.Power.Active,
                        unit: 'W'
                      }
                    : undefined,
                  amount: props.values?.LoadOnAcGrid
                    ? getAmount(
                        highestConnectionValue,
                        props.values.LoadOnAcGrid.Power.Active
                      )
                    : 0,
                  showValues: showValues
                }}
                isLast={false}
                isFirst={false}
              />
              {/*-------------------------------------------------------------------------------------------------------------------------------------------------------------*/}
              <TopologyColumn
                topBox={{
                  title: 'Pv Inverter',
                  data: props.values?.PvOnAcIsland
                    ? [
                        {
                          value: props.values.PvOnAcIsland.Power.Active,
                          unit: 'W'
                        }
                      ]
                    : undefined,
                  connected:
                    props.values.Config.Devices.PvOnAcIsland.DeviceState !==
                    'Disabled'
                }}
                topConnection={{
                  orientation: 'vertical',
                  position: 'top',
                  data: props.values?.PvOnAcIsland
                    ? {
                        value: props.values.PvOnAcIsland.Power.Active,
                        unit: 'W'
                      }
                    : undefined,
                  amount: props.values?.PvOnAcIsland
                    ? getAmount(
                        highestConnectionValue,
                        props.values.PvOnAcIsland.Power.Active
                      )
                    : 0,
                  showValues: showValues
                }}
                centerBox={{
                  title: 'Island Bus',
                  data: props.values?.AcDc
                    ? [
                        {
                          value: props.values.AcDc.Ac.L1.Voltage,
                          unit: 'V'
                        },
                        {
                          value: props.values.AcDc.Ac.L2.Voltage,
                          unit: 'V'
                        },
                        {
                          value: props.values.AcDc.Ac.L3.Voltage,
                          unit: 'V'
                        }
                      ]
                    : undefined,
                  connected: true
                }}
                centerConnection={{
                  orientation: 'horizontal',
                  data: props.values?.AcDc
                    ? {
                        value: props.values.AcDc.Dc.Power,
                        unit: 'W'
                      }
                    : undefined,
                  amount: props.values.AcDc
                    ? getAmount(
                        highestConnectionValue,
                        props.values.AcDc.Dc.Power
                      )
                    : 0,
                  showValues: showValues
                }}
                bottomBox={{
                  title: 'AC Loads',
                  data: props.values?.LoadOnAcIsland
                    ? [
                        {
                          value: props.values.LoadOnAcIsland.Ac.Power.Active,
                          unit: 'W'
                        }
                      ]
                    : undefined,
                  connected:
                    props.values.Config.Devices.IslandBusLoadMeterIp
                      .DeviceState != 'Disabled'
                }}
                bottomConnection={{
                  orientation: 'vertical',
                  position: 'bottom',
                  data: props.values?.LoadOnAcIsland
                    ? {
                        value: props.values.LoadOnAcIsland.Ac.Power.Active,
                        unit: 'W'
                      }
                    : undefined,
                  amount: props.values?.LoadOnAcIsland
                    ? getAmount(
                        highestConnectionValue,
                        props.values.LoadOnAcIsland.Ac.Power.Active
                      )
                    : 0,
                  showValues: showValues
                }}
                isLast={false}
                isFirst={false}
              />
              {/*-------------------------------------------------------------------------------------------------------------------------------------------------------------*/}
              <TopologyColumn
                centerBox={{
                  title: 'AC-DC',
                  data: props.values.AcDc
                    ? [
                        {
                          value: props.values.AcDc.Ac.L1.Power.Active,
                          unit: 'W'
                        },
                        {
                          value: props.values.AcDc.Ac.L2.Power.Active,
                          unit: 'W'
                        },
                        {
                          value: props.values.AcDc.Ac.L3.Power.Active,
                          unit: 'W'
                        }
                      ]
                    : undefined,
                  connected: true
                }}
                centerConnection={{
                  orientation: 'horizontal',
                  data: props.values?.AcDcToDcLink
                    ? {
                        value: props.values.AcDcToDcLink.Power,
                        unit: 'W'
                      }
                    : undefined,
                  amount: props.values?.AcDcToDcLink
                    ? getAmount(
                        highestConnectionValue,
                        props.values.AcDcToDcLink.Power
                      )
                    : 0,
                  showValues: showValues
                }}
                isLast={false}
                isFirst={false}
              />
              {/*-------------------------------------------------------------------------------------------------------------------------------------------------------------*/}
              <TopologyColumn
                topBox={{
                  title: 'Pv DC-DC',
                  data: props.values?.PvOnDc
                    ? [
                        {
                          value: totalPvPower,
                          unit: 'W'
                        }
                      ]
                    : undefined,
                  connected:
                    props.values.Config.Devices.PvOnDc.DeviceState != 'Disabled'
                }}
                topConnection={{
                  orientation: 'vertical',
                  position: 'top',
                  data: props.values?.PvOnDc
                    ? {
                        value: totalPvPower,
                        unit: 'W'
                      }
                    : undefined,
                  amount: props.values?.PvOnDc
                    ? getAmount(highestConnectionValue, totalPvPower)
                    : 0,
                  showValues: showValues
                }}
                centerBox={{
                  title: 'DC Link',
                  data: props.values?.DcDc
                    ? [
                        {
                          value: props.values.DcDc.Dc.Link.Voltage,
                          unit: 'W'
                        }
                      ]
                    : undefined,
                  connected: true
                }}
                centerConnection={{
                  orientation: 'horizontal',
                  data: props.values?.DcDc
                    ? {
                        value: props.values.DcDc.Dc.Link.Power,
                        unit: 'W'
                      }
                    : undefined,
                  amount: props.values?.DcDc
                    ? getAmount(
                        highestConnectionValue,
                        props.values.DcDc.Dc.Link.Power
                      )
                    : 0,
                  showValues: showValues
                }}
                bottomBox={{
                  title: 'DC Loads',
                  data: props.values?.LoadOnDc
                    ? [
                        {
                          value: props.values.LoadOnDc.Power,
                          unit: 'W'
                        }
                      ]
                    : undefined,
                  connected:
                    props.values.Config.Devices.LoadOnDc.DeviceState !=
                    'Disabled'
                }}
                bottomConnection={{
                  orientation: 'vertical',
                  position: 'bottom',
                  data: props.values?.LoadOnDc
                    ? {
                        value: props.values.LoadOnDc.Power,
                        unit: 'W'
                      }
                    : undefined,
                  amount: props.values?.LoadOnDc
                    ? getAmount(
                        highestConnectionValue,
                        props.values.LoadOnDc.Power
                      )
                    : 0,
                  showValues: showValues
                }}
                isLast={false}
                isFirst={false}
              />
              {/*-------------------------------------------------------------------------------------------------------------------------------------------------------------*/}

              <TopologyColumn
                centerBox={{
                  title: 'DC-DC',
                  data: props.values?.DcDc
                    ? [
                        {
                          value: props.values.DcDc.Dc.Battery.Voltage,
                          unit: 'V'
                        }
                      ]
                    : undefined,
                  connected: true
                }}
                centerConnection={{
                  orientation: 'horizontal',

                  data: props.values?.Battery
                    ? {
                        //value: props.values.Battery.Dc.Power for salimax,
                        value:
                          product == 0
                            ? props.values.Battery.Dc.Power
                            : props.values.Battery.Power,
                        unit: 'W'
                      }
                    : undefined,
                  amount: props.values?.Battery
                    ? getAmount(
                        highestConnectionValue,
                        product == 0
                          ? props.values.Battery.Dc.Power
                          : props.values.Battery.Power
                      )
                    : 0,
                  showValues: showValues
                }}
                isLast={false}
                isFirst={false}
              />
              <TopologyColumn
                centerBox={{
                  title: 'Battery',
                  data: props.values.Battery
                    ? [
                        {
                          value: props.values.Battery.Soc,
                          unit: '%'
                        },
                        {
                          //value: props.values.Battery.Dc.Voltage for salimax,
                          value:
                            product === 0
                              ? props.values.Battery.Dc.Voltage
                              : props.values.Battery.Voltage,
                          unit: 'V'
                        },
                        {
                          //value: props.values.Battery.Dc.Current for salimax,

                          value:
                            product == 0
                              ? props.values.Battery.Dc.Current
                              : props.values.Battery.Current,
                          unit: 'A'
                        },
                        {
                          //value: props.values.Battery.Temperature for salimax,
                          value:
                            product == 0
                              ? props.values.Battery.Temperature
                              : props.values.Battery.TemperatureCell1,
                          unit: '°C'
                        }
                        // {
                        //   value: Object.keys(props.values.Battery.Devices)
                        //     .length,
                        //   unit: ''
                        // }
                      ]
                    : undefined,

                  connected:
                    props.values.Config.Devices.BatteryIp.DeviceState !=
                    'Disabled'
                }}
                isLast={true}
                isFirst={false}
              />
            </Grid>
          </>
        )}
      </Grid>
    </Container>
  );
}

export default Topology;
